import { FC, type JSX } from 'react';

import { useTranslation } from '@/i18n';
import {
  AssigneeFilter,
  DateFilter,
  ListedTimelogs,
  ListedTimelogsContextProvider,
  TeamFilter,
  TimelogsByUserTable,
  TimelogsTotalsSummary,
  TypeFilter,
} from '@/ui/components/timelogs/listedTimelogs/listedTimelogsContext';
import AuthenticatedPageWrapper from '@/ui/layouts/authenticatedPageWrapper/AuthenticatedPageWrapper';
import CreateEditTimelogSidebar from '@/ui/pages/projectPage/components/LabourPanel/CreateEditTimelogSidebar';
import DeleteTimelogModal from '@/ui/pages/projectPage/components/LabourPanel/DeleteTimelogModal';
import { Button, ManageableItemsList, Switch } from '@payaca/ambrosia';
import { Helmet } from 'react-helmet';

const TimelogReportingPage: FC = (): JSX.Element => {
  const translate = useTranslation();

  return (
    <>
      <Helmet title={translate('pages.timelogs.title')} />
      <AuthenticatedPageWrapper className="timelog-reporting-page">
        <div className="p-4">
          {/* Not read-only: this page supports editing/deleting timelogs
              inline (via the sidebar/modal mounted below). Row edit/delete
              affordances are permission-gated inside ListedTimelogs. */}
          <ListedTimelogsContextProvider pageSize={50}>
            {(state) => (
              <>
                <ManageableItemsList>
                  <ManageableItemsList.HeaderBar
                    heading="Timelogs"
                    buttons={<TimelogsTotalsSummary />}
                  />
                  <ManageableItemsList.ActionBar className="justify-between">
                    {/* Filters apply to both the list and by-user views. */}
                    <div className="flex flex-wrap items-center gap-3">
                      <TeamFilter />
                      <AssigneeFilter />
                      <DateFilter />
                      <TypeFilter />
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={state.reset}
                      >
                        Reset
                      </Button>
                    </div>
                    <label className="flex cursor-pointer items-center gap-2.5 whitespace-nowrap">
                      <Switch
                        value={state.viewMode === 'byUser'}
                        onChange={(on) =>
                          state.setViewMode(on ? 'byUser' : 'list')
                        }
                      />
                      Group by user
                    </label>
                  </ManageableItemsList.ActionBar>

                  {state.viewMode === 'byUser' ? (
                    <TimelogsByUserTable />
                  ) : (
                    <ListedTimelogs />
                  )}
                </ManageableItemsList>

                {/* Editing an existing timelog preserves its linked entity, so
                    no project context is needed here (account-wide page). */}
                <CreateEditTimelogSidebar
                  isOpen={state.activeDialog?.type === 'editTimelog'}
                  timelog={
                    state.activeDialog?.type === 'editTimelog'
                      ? state.activeDialog.timelog
                      : undefined
                  }
                  onClose={() => state.closeDialog()}
                />

                <DeleteTimelogModal
                  isOpen={state.activeDialog?.type === 'deleteTimelog'}
                  timelogId={
                    state.activeDialog?.type === 'deleteTimelog'
                      ? state.activeDialog.timelog.id
                      : undefined
                  }
                  onClose={() => state.closeDialog()}
                />
              </>
            )}
          </ListedTimelogsContextProvider>
        </div>
      </AuthenticatedPageWrapper>
    </>
  );
};

export default TimelogReportingPage;
